/**
 * DockedTrendChart.ts
 * Low-profile, wide horizontal trend chart docked directly above the Master Date/Time Control Bar.
 * Features:
 * - Wide horizontal real estate (~85px total height) with obsidian semi-transparency.
 * - Displays the most recently activated product's diurnal trend curve.
 * - 5-Day and 10-Day horizon toggle pills.
 * - In-place Model Selector dropdown for that product.
 * - Model Comparison button [⚖️ Compare] which opens ModelComparisonDrawer.
 * - POI coordinates & elevation readout.
 * - Collapsible with [^] chevron to minimize to a slim 26px handle.
 */

import store, { AppState, setProductModel, setTrendRefLinesEnabled } from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import { ModelComparisonDrawer } from './panels/ModelComparisonDrawer';
import { LegendTrendChartService } from '../services/LegendTrendChartService';
import { TierAccessService } from '../services/TierAccessService';

export class DockedTrendChart {
  private container: HTMLElement;
  private headerEl: HTMLElement;
  private titleEl: HTMLElement;
  private tabsContainerEl: HTMLElement;
  private modelControlsEl: HTMLElement;
  private refLinesBtn: HTMLButtonElement;
  private poiEl: HTMLElement;
  private toggleBtn: HTMLButtonElement;
  private canvasWrapEl: HTMLElement;

  private currentProduct: string = 'hrtemp';
  private isCollapsed: boolean = false;
  private lastRenderSig: string = '';

  constructor(private parentElement: HTMLElement) {
    this.container = document.createElement('div');
    this.container.id = 'timeline-trend-dock';
    this.container.className = 'timeline-trend-dock';

    this.isCollapsed = typeof window !== 'undefined' && localStorage.getItem('skyskout_docked_trend_collapsed') === 'true';
    if (this.isCollapsed) {
      this.container.classList.add('collapsed');
    }

    // 1. Header Bar
    this.headerEl = document.createElement('div');
    this.headerEl.className = 'docked-trend-header';

    const headerLeft = document.createElement('div');
    headerLeft.className = 'docked-trend-header-left';

    this.tabsContainerEl = document.createElement('div');
    this.tabsContainerEl.className = 'docked-trend-tabs-strip';

    this.titleEl = document.createElement('span');
    this.titleEl.className = 'docked-trend-title';

    this.modelControlsEl = document.createElement('div');
    this.modelControlsEl.className = 'docked-trend-model-group';

    this.refLinesBtn = document.createElement('button');
    this.refLinesBtn.type = 'button';
    this.refLinesBtn.className = 'docked-trend-reflines-btn active';
    this.refLinesBtn.title = 'Toggle Min, Midpoint & Max reference lines';
    this.refLinesBtn.onclick = (e) => {
      e.stopPropagation();
      const current = store.getState().trendRefLinesEnabled !== false;
      setTrendRefLinesEnabled(!current);
    };

    headerLeft.appendChild(this.tabsContainerEl);
    headerLeft.appendChild(this.titleEl);
    headerLeft.appendChild(this.modelControlsEl);
    headerLeft.appendChild(this.refLinesBtn);

    // Right section: POI indicator + Toggle Button floating on right
    const headerRight = document.createElement('div');
    headerRight.className = 'docked-trend-header-right';

    this.poiEl = document.createElement('span');
    this.poiEl.className = 'docked-trend-poi';

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.type = 'button';
    this.toggleBtn.className = 'docked-trend-toggle-btn';
    this.toggleBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleCollapse();
    };
    this.updateToggleBtnState();

    headerRight.appendChild(this.poiEl);
    headerRight.appendChild(this.toggleBtn);

    this.headerEl.appendChild(headerLeft);
    this.headerEl.appendChild(headerRight);

    // 2. Canvas Wrapper
    this.canvasWrapEl = document.createElement('div');
    this.canvasWrapEl.className = 'docked-trend-canvas-wrap';

    this.container.appendChild(this.headerEl);
    this.container.appendChild(this.canvasWrapEl);

    // Mount to parent (e.g. #timeline-container before .timeline-row)
    if (this.parentElement.firstChild) {
      this.parentElement.insertBefore(this.container, this.parentElement.firstChild);
    } else {
      this.parentElement.appendChild(this.container);
    }

    this.setupSubscriptions();
    this.update(store.getState());

    // Observe timeline container resize to dynamically dock the layer drawer
    const timelineEl = document.getElementById('timeline-container');
    if (timelineEl && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        this.syncTimelineHeight();
      });
      ro.observe(timelineEl);
    }
    this.syncTimelineHeight();
  }

  private syncTimelineHeight(): void {
    const el = document.getElementById('timeline-container');
    if (el) {
      document.documentElement.style.setProperty('--timeline-height', `${el.offsetHeight + 2}px`);
    }
  }

  private updateToggleBtnState(): void {
    if (this.isCollapsed) {
      this.container.classList.add('collapsed');
      this.toggleBtn.title = 'Expand Forecast Trend Chart';
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.toggleBtn.innerHTML = `
        <span class="docked-trend-toggle-arrow">▲</span>
        <span class="docked-trend-toggle-text">Forecast Trend</span>
        <span class="docked-trend-toggle-arrow">▲</span>
      `;
    } else {
      this.container.classList.remove('collapsed');
      this.toggleBtn.title = 'Hide Forecast Trend Chart';
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.toggleBtn.innerHTML = `
        <span class="docked-trend-toggle-arrow">▼</span>
        <span class="docked-trend-toggle-text">Hide Trend Chart</span>
        <span class="docked-trend-toggle-arrow">▼</span>
      `;
    }
  }

  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.updateToggleBtnState();
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('skyskout_docked_trend_collapsed', String(this.isCollapsed));
      } catch (_) {}
    }
    store.update({ dockedTrendCollapsed: this.isCollapsed });
    this.syncTimelineHeight();
    setTimeout(() => this.syncTimelineHeight(), 50);
    setTimeout(() => this.syncTimelineHeight(), 220);
    // Trigger chart redraw when expanded
    if (!this.isCollapsed) {
      setTimeout(() => {
        LegendTrendChartService.updateCharts(store.getState());
      }, 50);
    }
  }

  public update(state: AppState): void {
    // 0. Sync collapse state with store if changed externally
    if (state.dockedTrendCollapsed !== undefined && state.dockedTrendCollapsed !== this.isCollapsed) {
      this.isCollapsed = state.dockedTrendCollapsed;
      this.updateToggleBtnState();
      this.syncTimelineHeight();
    }

    // 1. Determine active forecast layers to display
    const activeForecastLayers = this.getActiveForecastLayers(state);

    if (activeForecastLayers.length === 0) {
      this.container.style.display = 'none';
      if (this.currentProduct) {
        LegendTrendChartService.destroyChart(this.currentProduct);
      }
      this.syncTimelineHeight();
      return;
    }
    this.container.style.display = '';

    const lastActiveCanonical = state.lastActivatedProduct ? this.canonicalizeProduct(state.lastActivatedProduct) : null;
    let product = (lastActiveCanonical && activeForecastLayers.includes(lastActiveCanonical))
      ? lastActiveCanonical
      : activeForecastLayers[activeForecastLayers.length - 1];

    const isChartSupported = LegendTrendChartService.isSupportedLayer(product);

    const poi = state.selectedPoi;
    const horizonDays = state.timelineHorizon === 10 ? 10 : 5;
    let defaultModelForProd = 'nbm';
    if (product === 'sunrise' || product === 'alpenglow' || product === 'goldenhour') defaultModelForProd = 'gw_v2_blend_50';
    else if (product === 'darkskies') defaultModelForProd = 'blend';
    else if (product === 'clouds') defaultModelForProd = (state.cloudModels && state.cloudModels[0]) || state.cloudModel || 'ecmwf';
    else if (product === 'smoke' || product === 'aqi') defaultModelForProd = state.smokeModel || 'hrrr';
    else if (product === 'optics_dew_risk' || product === 'seeing') defaultModelForProd = 'rdps';
    else if (product === 'turbulence') defaultModelForProd = 'gfs';
    const currentModel = state.selectedModels[product] || (product === 'turbulence' ? state.selectedModels['aviation_turbulence'] : undefined) || defaultModelForProd;

    const userTier = TierAccessService.getUserAccessTier(state);
    const renderSig = `${product}_${activeForecastLayers.join(',')}_${poi ? `${poi.lat.toFixed(3)}_${poi.lng.toFixed(3)}` : 'none'}_${horizonDays}_${currentModel}_${state.alpenglowMode || 'goldenhour'}_${this.isCollapsed}_${state.trendRefLinesEnabled !== false}_${isChartSupported}_${userTier}_${Boolean(state.sessionToken)}`;

    const productChanged = this.currentProduct !== product;
    this.currentProduct = product;

    // Update Header Content
    this.updateHeaderContent(product, activeForecastLayers, state, horizonDays, isChartSupported);

    // Update POI Display
    if (poi) {
      const units = state.units || 'imperial';
      const elevDisplay = units === 'metric' ? `${Math.round(poi.elev / 3.28084).toLocaleString()}m` : `${Math.round(poi.elev).toLocaleString()}ft`;
      this.poiEl.innerHTML = `📍 <span>${poi.lat.toFixed(3)}°N, ${poi.lng.toFixed(3)}°W</span> <span class="docked-trend-elev">(${elevDisplay})</span>`;
      this.poiEl.style.display = 'inline-flex';
    } else {
      this.poiEl.style.display = 'none';
    }

    if (this.isCollapsed) {
      return;
    }

    if (!isChartSupported) {
      if (this.lastRenderSig !== renderSig || productChanged) {
        this.lastRenderSig = renderSig;
        LegendTrendChartService.destroyChart(product);
        const titlesMap: Record<string, string> = {
          turbulence: 'Aviation Turbulence',
          aviation_turbulence: 'Aviation Turbulence',
          jet_stream: 'Jet Stream & Winds Aloft',
          precip_accum: 'Precipitation Accumulation',
          rain_accum: 'Precipitation Accumulation',
          snow_accum: 'Snow Accumulation'
        };
        const iconsMap: Record<string, string> = {
          turbulence: '✈️',
          aviation_turbulence: '✈️',
          jet_stream: '🌀',
          precip_accum: '🌧️',
          rain_accum: '🌧️',
          snow_accum: '❄️'
        };
        const productLabel = titlesMap[product] || 'this product';
        const icon = iconsMap[product] || '📊';
        this.canvasWrapEl.innerHTML = `
          <div class="docked-trend-unavailable">
            <div class="docked-trend-unavailable-content">
              <div class="docked-trend-unavailable-icon">${icon}</div>
              <div class="docked-trend-unavailable-text">
                <div class="docked-trend-unavailable-title">Trend Chart Unavailable for this product</div>
                <div class="docked-trend-unavailable-desc">Point forecast trend telemetry is not available for ${productLabel}. You can still explore spatial layers across the map canvas.</div>
              </div>
            </div>
          </div>
        `;
      }
      return;
    }

    // Update Canvas DOM if product changed or not mounted
    const existingCanvas = document.getElementById(`legend-${product}-chart`);
    if (!existingCanvas || productChanged) {
      // Clean previous canvases
      this.canvasWrapEl.innerHTML = `
        <div id="legend-${product}-loading" class="docked-trend-loading">Loading forecast trend...</div>
        <canvas id="legend-${product}-chart" class="docked-trend-canvas"></canvas>
      `;
    }

    if (this.lastRenderSig !== renderSig) {
      this.lastRenderSig = renderSig;
      LegendTrendChartService.updateCharts(state);
    }
  }

  private canonicalizeProduct(k: string): string {
    return DockedTrendChart.CANONICAL_MAP[k] || k;
  }

  private static readonly CANONICAL_MAP: Record<string, string> = {
    // Precipitation aliases
    precip_forecast: 'precip',
    precip_prob: 'precip',
    precip_rate: 'precip',
    precip_24h: 'precip',
    snow_24h: 'precip',

    // Temperature aliases
    hrtemp_current: 'hrtemp',
    hrtemp_forecast: 'hrtemp',
    hrtemp_contours: 'hrtemp',

    // Surface Wind aliases
    winds_surface_current: 'winds',
    winds_forecast: 'winds',

    // Celestial & Optics
    alpenglow_forecast: 'alpenglow',
    sunrise_forecast: 'sunrise',
    goldenhour_forecast: 'goldenhour',
    lenticular_forecast: 'lenticular',

    // Clouds & Fog
    clouds_forecast: 'clouds',
    cloud_forecast: 'clouds',
    fog_forecast: 'valley_fog',
    valley_fog_forecast: 'valley_fog',
    cloud_inversion_forecast: 'cloud_inversion',
    inversion_forecast: 'cloud_inversion',
    cloud_base_forecast: 'cloud_base',

    // Smoke & AQI
    smoke_forecast: 'smoke',
    aqi_forecast: 'aqi',
    aqi_index: 'aqi',

    // Stargazing / Astronomy
    darkskies_forecast: 'darkskies',
    optics_dew_risk_forecast: 'optics_dew_risk',
    seeing_forecast: 'seeing',

    // Other forecast fields
    freezing_level_forecast: 'freezing_level',
    relative_humidity_forecast: 'relative_humidity',
    dewpoint_forecast: 'dewpoint',
    windchill_forecast: 'windchill',
    sun_intensity_forecast: 'sun_intensity',
    aviation_turbulence: 'turbulence',
    turbulence_forecast: 'turbulence'
  };

  private static readonly NON_FORECAST_PRODUCTS: Set<string> = new Set([
    // Water & Hydrology (Observed)
    'reservoir', 'reservoir_levels', 'water_storage',
    'streamflow', 'streamflow_gauges', 'live_weather_streamflow',
    
    // AQI Stations (Observed)
    'aqi_stations', 'live_weather_aqi_stations',
    
    // Radar & Satellite (Observed)
    'doppler_radar', 'radar', 'mrms', 'live_weather_radar', 'radar_mrms',
    'satellite', 'live_weather_satellite', 'satellite_imagery', 'goes',
    'sst', 'sea_surface_temp',
    
    // Hazards & Fire (Observed / Advisory)
    'fire_restrictions', 'wildfires', 'fire_perimeters', 'thermal_hotspots',
    'avalanche', 'avalanche_danger',
    
    // Observed Snow & Stations
    'snow_depth', 'snowpack', 'observed_snow_depth', 'snow_stations', 'snotel',
    
    // Phenology & Foraging (Outlooks / Seasonal Maps)
    'mushrooms', 'mushrooms_outlook',
    'foliage', 'alpine_wildflowers', 'desert_wildflowers', 'wild_berries',
    
    // Miscellaneous Tools & Rasters without point telemetry
    'solar_lunar_tools',
    'rtma', 'rtma_surface', 'rtma_temp', 'rtma_winds',
    'precip_accum', 'rain_accum', 'snow_accum',
    'jet_stream'
  ]);

  private getActiveForecastLayers(state: AppState): string[] {
    const raw: string[] = [];
    const s = state as any;
    if (state.primaryProduct) raw.push(state.primaryProduct);
    if (state.cloudsEnabled) raw.push('clouds');
    if (state.windsEnabled) raw.push('winds');
    if (state.smokeEnabled) raw.push('smoke');
    if (s.precipEnabled) raw.push('precip');
    if (s.lightningEnabled || s.thunderstormEnabled) raw.push('lightning');
    if (s.lenticularEnabled) raw.push('lenticular');
    if (s.darkskiesEnabled) raw.push('darkskies');
    if (s.alpenglowEnabled) raw.push('alpenglow');
    if (s.sunriseEnabled) raw.push('sunrise');
    if (s.goldenhourEnabled) raw.push('goldenhour');
    if (s.valleyFogEnabled) raw.push('valley_fog');
    if (s.cloudInversionEnabled) raw.push('cloud_inversion');
    if (s.cloudBaseEnabled) raw.push('cloud_base');
    if (s.opticsDewRiskEnabled) raw.push('optics_dew_risk');
    if (s.freezingLevelEnabled) raw.push('freezing_level');
    if (s.relativeHumidityEnabled) raw.push('relative_humidity');
    if (s.dewpointEnabled) raw.push('dewpoint');
    if (s.windchillEnabled) raw.push('windchill');
    if (s.sunIntensityEnabled) raw.push('sun_intensity');
    if (s.aqiEnabled) raw.push('aqi');
    if (state.activeLayerProducts) {
      Object.keys(state.activeLayerProducts).forEach(k => {
        if (state.activeLayerProducts[k] === true) {
          raw.push(k);
        }
      });
    }

    const canonicalList: string[] = [];
    const processKey = (k: string) => {
      if (DockedTrendChart.NON_FORECAST_PRODUCTS.has(k)) return;
      const prod = this.canonicalizeProduct(k);
      if (DockedTrendChart.NON_FORECAST_PRODUCTS.has(prod)) return;
      if (!LegendTrendChartService.isSupportedLayer(prod)) return;
      if (prod === 'sunrise' || prod === 'alpenglow' || prod === 'goldenhour') {
        const targetProd = (prod === 'alpenglow' && state.alpenglowMode === 'goldenhour') ? 'goldenhour' : prod;
        const activeModel = String(state.selectedModels?.[targetProd] || state.selectedModels?.[prod] || '').toLowerCase();
        if (activeModel === 'gw_nowcast') {
          return; // Suppress multi-day trend chart for single-event Live NowCast
        }
      }
      if (!canonicalList.includes(prod)) {
        canonicalList.push(prod);
      }
    };

    raw.forEach(processKey);

    if (state.activeLegendOrder) {
      state.activeLegendOrder.forEach(processKey);
    }

    return canonicalList;
  }

  private updateHeaderContent(product: string, activeForecastLayers: string[], state: AppState, horizonDays: number, isChartSupported: boolean = true): void {
    const titles: Record<string, { label: string; icon: string }> = {
      sunrise: { label: 'Sunrise & Sunset Quality', icon: '🌅' },
      alpenglow: {
        label: state.alpenglowMode === 'goldenhour' ? 'Golden Hour Glow Trend' : 'Alpine Alpenglow Trend',
        icon: state.alpenglowMode === 'goldenhour' ? '🌅' : '🏔️'
      },
      goldenhour: { label: 'Golden Hour Glow Trend', icon: '🌅' },
      hrtemp: { label: 'Temperature Trend Chart', icon: '📈' },
      lightning: { label: 'Convective Lightning Trend', icon: '⚡' },
      thunderstorm: { label: 'Thunderstorm Risk Trend', icon: '⛈️' },
      clouds: { label: 'Cloud Cover Trend', icon: '☁️' },
      winds: { label: 'Surface Winds & Gusts Trend', icon: '💨' },
      precip: {
        label: state.precipViewMode === 'prob'
          ? 'Precip Probability Trend (PoP)'
          : (state.precipViewMode === '24h_snow'
            ? 'Daily Snow Accumulation Trend'
            : (state.precipViewMode === '24h' || state.precipViewMode === '24h_rain'
              ? 'Daily Precip Accumulation Trend'
              : 'Precipitation Trend')),
        icon: '🌧️'
      },
      precip_accum: { label: 'Precipitation Accumulation', icon: '🌧️' },
      snow_accum: { label: 'Snow Accumulation', icon: '❄️' },
      rain_accum: { label: 'Precipitation Accumulation', icon: '🌧️' },
      relative_humidity: { label: 'Relative Humidity Trend', icon: '💧' },
      dewpoint: { label: 'Dew Point Temperature Trend', icon: '🌫️' },
      optics_dew_risk: { label: 'Optics Dew Risk Trend', icon: '🔭' },
      seeing: { label: 'Astronomical Seeing Trend', icon: '✨' },
      freezing_level: { label: 'Freezing Level Altitude', icon: '❄️' },
      windchill: { label: 'Wind Chill Index Trend', icon: '🥶' },
      smoke: { label: 'Surface Smoke & AQI Trend', icon: '🔥' },
      aqi: { label: 'Air Quality Index Trend', icon: '🫁' },
      darkskies: { label: 'Dark Skies Stargazing Trend', icon: '🌌' },
      sun_intensity: { label: 'Solar Irradiance / UV Trend', icon: '☀️' },
      valley_fog: { label: 'Valley Fog Probability Trend', icon: '🌫️' },
      cloud_inversion: { label: 'Sea of Clouds Inversion Trend', icon: '🏔️' },
      cloud_base: { label: 'Cloud Base Ceiling Altitude', icon: '☁️' },
      lenticular: { label: 'Lenticular Cloud Index', icon: '🛸' },
      turbulence: { label: 'Aviation Turbulence', icon: '✈️' },
      aviation_turbulence: { label: 'Aviation Turbulence', icon: '✈️' },
      jet_stream: { label: 'Jet Stream & Winds Aloft', icon: '🌀' }
    };

    const tabShortLabels: Record<string, { label: string; icon: string }> = {
      sunrise: { label: 'Sunrise', icon: '🌅' },
      alpenglow: {
        label: state.alpenglowMode === 'goldenhour' ? 'Golden Hour' : 'Alpenglow',
        icon: state.alpenglowMode === 'goldenhour' ? '🌅' : '🏔️'
      },
      goldenhour: { label: 'Golden Hour', icon: '🌅' },
      hrtemp: { label: 'Temps', icon: '📈' },
      lightning: { label: 'Lightning', icon: '⚡' },
      thunderstorm: { label: 'Thunderstorms', icon: '⛈️' },
      clouds: { label: 'Clouds', icon: '☁️' },
      winds: { label: 'Winds', icon: '💨' },
      precip: { label: 'Precip', icon: '🌧️' },
      precip_accum: { label: 'Precip Accum', icon: '🌧️' },
      snow_accum: { label: 'Snow Accum', icon: '❄️' },
      rain_accum: { label: 'Precip Accum', icon: '🌧️' },
      relative_humidity: { label: 'Humidity', icon: '💧' },
      dewpoint: { label: 'Dew Point', icon: '🌫️' },
      optics_dew_risk: { label: 'Dew Risk', icon: '🔭' },
      seeing: { label: 'Seeing', icon: '✨' },
      freezing_level: { label: 'Freezing Level', icon: '❄️' },
      windchill: { label: 'Wind Chill', icon: '🥶' },
      smoke: { label: 'Smoke', icon: '🔥' },
      aqi: { label: 'AQI', icon: '🫁' },
      darkskies: { label: 'Dark Skies', icon: '🌌' },
      sun_intensity: { label: 'UV / Solar', icon: '☀️' },
      valley_fog: { label: 'Valley Fog', icon: '🌫️' },
      cloud_inversion: { label: 'Inversion', icon: '🏔️' },
      cloud_base: { label: 'Cloud Base', icon: '☁️' },
      lenticular: { label: 'Lenticular', icon: '🛸' },
      turbulence: { label: 'Turbulence', icon: '✈️' },
      aviation_turbulence: { label: 'Turbulence', icon: '✈️' },
      jet_stream: { label: 'Jet Stream', icon: '🌀' }
    };

    const meta = titles[product] || { label: 'Forecast Trend Chart', icon: '📈' };

    // When 2 or more forecast layers are active, render the interactive Tab Strip
    if (activeForecastLayers.length > 1) {
      this.titleEl.style.display = 'none';
      this.tabsContainerEl.style.display = 'inline-flex';
      this.tabsContainerEl.innerHTML = '';

      activeForecastLayers.forEach(prodKey => {
        const isSelected = (prodKey === product);
        const itemMeta = tabShortLabels[prodKey] || titles[prodKey] || { label: prodKey, icon: '📈' };

        let defaultM = 'nbm';
        if (prodKey === 'sunrise' || prodKey === 'alpenglow' || prodKey === 'goldenhour') defaultM = 'gw_v2_blend_50';
        else if (prodKey === 'darkskies') defaultM = 'blend';
        else if (prodKey === 'turbulence' || prodKey === 'jet_stream') defaultM = 'gfs';
        else if (prodKey === 'clouds') defaultM = (state.cloudModels && state.cloudModels[0]) || state.cloudModel || 'ecmwf';
        else if (prodKey === 'smoke') defaultM = state.smokeModel || 'hrrr';
        else if (prodKey === 'optics_dew_risk' || prodKey === 'seeing') defaultM = 'rdps';
        else if (prodKey === 'snow_accum') defaultM = state.snowAccumModel || 'ecmwf';
        else if (prodKey === 'precip_accum') defaultM = state.precipAccumModel || 'cmorph2';

        let qProd = prodKey;
        if (prodKey === 'precip') {
          const pMode = state.precipViewMode || 'hourly';
          if (pMode === 'prob') qProd = 'precip_prob';
          else if (pMode === '24h_snow') qProd = 'snow_24h';
          else if (pMode === '24h' || pMode === '24h_rain') qProd = 'precip_24h';
        }
        const activeModel = (state.selectedModels[qProd] || state.selectedModels[prodKey] || defaultM).toLowerCase();
        const badgeLabel = (activeModel === 'gw_v2_blend_50' || activeModel === 'blend_50' || activeModel === 'skysk_hres_topo')
          ? 'SKYSKOUT HRES'
          : activeModel.replace(/^gw_v2_/, '').toUpperCase();

        const tabBtn = document.createElement('button');
        tabBtn.type = 'button';
        tabBtn.className = `docked-trend-tab ${isSelected ? 'active' : ''}`;
        tabBtn.title = `Switch forecast trend chart to ${titles[prodKey]?.label || itemMeta.label}`;
        tabBtn.innerHTML = `
          <span class="docked-trend-tab-icon">${itemMeta.icon}</span>
          <span class="docked-trend-tab-label">${itemMeta.label}</span>
          <span class="docked-trend-tab-badge">${badgeLabel}</span>
        `;
        tabBtn.onclick = (e) => {
          e.stopPropagation();
          store.update({ lastActivatedProduct: prodKey });
        };
        this.tabsContainerEl.appendChild(tabBtn);
      });
    } else {
      // Single active forecast layer - render clean single title
      this.tabsContainerEl.style.display = 'none';
      this.titleEl.style.display = 'inline-flex';
      if (isChartSupported) {
        this.titleEl.innerHTML = `<span class="docked-trend-icon">${meta.icon}</span> <span>${meta.label} (${horizonDays}D)</span>`;
      } else {
        this.titleEl.innerHTML = `<span class="docked-trend-icon">${meta.icon}</span> <span>${meta.label}</span>`;
      }
    }

    // Model Selector & [⚖️ Compare] Button
    const manifest = state.manifest;
    let queryProduct = (product === 'alpenglow' && state.alpenglowMode === 'goldenhour') ? 'goldenhour' : product;
    if (queryProduct === 'aviation_turbulence') queryProduct = 'turbulence';
    if (product === 'precip') {
      const pMode = state.precipViewMode || 'hourly';
      if (pMode === 'prob') {
        queryProduct = 'precip_prob';
      } else if (pMode === '24h_snow') {
        queryProduct = 'snow_24h';
      } else if (pMode === '24h' || pMode === '24h_rain') {
        queryProduct = 'precip_24h';
      }
    }
    const availableModels = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, queryProduct);
    let defaultModelForProd = 'nbm';
    if (product === 'sunrise' || product === 'alpenglow' || product === 'goldenhour') defaultModelForProd = 'gw_v2_blend_50';
    else if (product === 'darkskies') defaultModelForProd = 'blend';
    else if (product === 'turbulence' || product === 'jet_stream') defaultModelForProd = 'gfs';
    const rawSelectedModel = (state.selectedModels[queryProduct] || state.selectedModels[product] || defaultModelForProd).toLowerCase();
    const hasModel = availableModels.some(m => m.id.toLowerCase() === rawSelectedModel);
    const selectedModel = hasModel ? rawSelectedModel : ((availableModels[0]?.id) || defaultModelForProd).toLowerCase();

    if (availableModels && availableModels.length > 0) {
      const modelsSig = availableModels.map(m => m.id).join(',');
      const prevRenderedProd = this.modelControlsEl.getAttribute('data-rendered-product');
      const prevRenderedModels = this.modelControlsEl.getAttribute('data-rendered-models');

      if (prevRenderedProd !== queryProduct || prevRenderedModels !== modelsSig) {
        this.modelControlsEl.setAttribute('data-rendered-product', queryProduct);
        this.modelControlsEl.setAttribute('data-rendered-models', modelsSig);
        this.modelControlsEl.innerHTML = `
          <div class="docked-model-select-wrap">
            <label class="docked-model-label">Model:</label>
            <select class="docked-trend-model-select" data-product="${queryProduct}">
              ${availableModels.map(m => {
                const isSelected = m.id.toLowerCase() === selectedModel;
                return `<option value="${m.id}" ${isSelected ? 'selected style="font-weight: 800 !important;"' : 'style="font-weight: 500;"'}>${m.label}</option>`;
              }).join('')}
            </select>
            ${(availableModels.length > 1 && isChartSupported) ? `
              <button type="button" class="docked-trend-compare-btn" title="Compare all forecast models for ${queryProduct}">
                ⚖️ Model Comparison
              </button>
            ` : ''}
          </div>
        `;

        const selectEl = this.modelControlsEl.querySelector('.docked-trend-model-select') as HTMLSelectElement;
        if (selectEl) {
          selectEl.addEventListener('change', (e) => {
            e.stopPropagation();
            const newModel = selectEl.value;
            setProductModel(queryProduct, newModel, true);
          });
        }

        const compareBtn = this.modelControlsEl.querySelector('.docked-trend-compare-btn') as HTMLElement;
        if (compareBtn) {
          compareBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            ModelComparisonDrawer.getInstance().open(queryProduct, store.getState().selectedPoi);
          });
        }
      } else {
        // Do not destroy/rebuild DOM if product & model list haven't changed:
        // this prevents Firefox from throwing TypeError in SelectChild.sys.mjs on active selects
        const selectEl = this.modelControlsEl.querySelector('.docked-trend-model-select') as HTMLSelectElement;
        if (selectEl && selectEl.value.toLowerCase() !== selectedModel) {
          selectEl.value = selectedModel;
        }
      }
    } else {
      this.modelControlsEl.removeAttribute('data-rendered-product');
      this.modelControlsEl.removeAttribute('data-rendered-models');
      this.modelControlsEl.innerHTML = '';
    }

    // Min/Max/Mid Reference Lines Toggle Button (for line charts)
    const isLineProduct = isChartSupported && product !== 'sunrise' && product !== 'alpenglow' && product !== 'goldenhour';
    if (isLineProduct) {
      const isRefLinesOn = state.trendRefLinesEnabled !== false;
      this.refLinesBtn.style.display = 'inline-flex';
      this.refLinesBtn.className = `docked-trend-reflines-btn ${isRefLinesOn ? 'active' : ''}`;
      this.refLinesBtn.innerHTML = `📏 Min/Max/Mid: ${isRefLinesOn ? 'ON' : 'OFF'}`;
    } else {
      this.refLinesBtn.style.display = 'none';
    }
  }

  private setupSubscriptions(): void {
    store.subscribe((state) => {
      this.update(state);
    });
  }
}

export default DockedTrendChart;
